/**
 * =============================================================================
 * R2 STORAGE SERVICE
 * =============================================================================
 *
 * Frontend service for interacting with Cloudflare R2 storage via Netlify Functions.
 * All R2 operations are proxied through Netlify Functions to keep credentials secure.
 *
 * ARCHITECTURE:
 *   Browser → Netlify Function → Cloudflare R2
 *
 * =============================================================================
 */

import type { Document, ProductType } from '@/types';

// API base URL for Netlify Functions
const API_BASE_URL = import.meta.env.VITE_API_URL || '/.netlify/functions';

// =============================================================================
// TYPES
// =============================================================================

export interface DocumentFormData {
  name: string;
  type: string;
  description: string;
  productType: ProductType;
  sortOrder: number;
  required: boolean;
  products: string[];
  active: boolean;
}

export interface UploadResponse {
  success: boolean;
  document?: Document;
  error?: string;
}

export interface DeleteResponse {
  success: boolean;
  error?: string;
}

export interface CoverPageStatus {
  exists: boolean;
  size?: number;
}

// =============================================================================
// DOCUMENT UPLOAD
// =============================================================================

/**
 * Upload a PDF document to R2 storage
 *
 * @param file - The PDF file to upload
 * @param metadata - Document metadata
 * @returns Upload response with created document or error
 */
export async function uploadDocument(
  file: File,
  metadata: DocumentFormData
): Promise<UploadResponse> {
  try {
    // Validate file type
    if (file.type !== 'application/pdf') {
      return {
        success: false,
        error: 'Only PDF files are allowed',
      };
    }

    // Validate file size (50MB max)
    const MAX_FILE_SIZE = 50 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      return {
        success: false,
        error: 'File size exceeds 50MB limit',
      };
    }

    // Convert file to base64
    const base64 = await fileToBase64(file);

    // Send to Netlify Function
    const response = await fetch(`${API_BASE_URL}/upload-document`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file: base64,
        filename: file.name,
        metadata,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Upload failed',
      };
    }

    return {
      success: true,
      document: result.document,
    };
  } catch (error) {
    console.error('Upload error:', error);
    return {
      success: false,
      error: 'Failed to upload document. Please try again.',
    };
  }
}

// =============================================================================
// DOCUMENT DELETE
// =============================================================================

/**
 * Delete a document from R2 storage
 *
 * @param documentId - The ID of the document to delete
 * @returns Delete response
 */
export async function deleteDocument(documentId: string): Promise<DeleteResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/delete-document`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ documentId }),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Delete failed',
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete error:', error);
    return {
      success: false,
      error: 'Failed to delete document. Please try again.',
    };
  }
}

// =============================================================================
// DOCUMENT FETCH
// =============================================================================

/**
 * Fetch documents from R2 storage
 *
 * @param category - Optional product type filter
 * @param includeInactive - Include inactive documents
 * @returns Array of documents
 */
export async function fetchDocumentsFromR2(
  category?: ProductType,
  includeInactive = false
): Promise<Document[]> {
  try {
    let url = `${API_BASE_URL}/get-documents`;
    const params = new URLSearchParams();

    if (category) {
      params.append('category', category);
    }
    if (includeInactive) {
      params.append('includeInactive', 'true');
    }

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to fetch documents');
    }

    const result = await response.json();
    return result.documents || [];
  } catch (error) {
    console.error('Fetch documents error:', error);
    return [];
  }
}

// =============================================================================
// DOCUMENT PREVIEW
// =============================================================================

/**
 * Get the preview URL for a document
 *
 * @param document - The document object
 * @returns URL for previewing the document
 */
export function getDocumentPreviewUrl(document: Document): string {
  // If document has r2Key (extended property), use the proxy function
  const r2Key = (document as Document & { r2Key?: string }).r2Key;
  if (r2Key) {
    return `${API_BASE_URL}/get-document-url?key=${encodeURIComponent(r2Key)}`;
  }
  // Otherwise, use the direct URL (for legacy documents)
  return document.url;
}

// =============================================================================
// COVER PAGE MANAGEMENT
// =============================================================================

export interface CoverPageStatusResult {
  'structural-floor': CoverPageStatus;
  'underlayment': CoverPageStatus;
}

/**
 * Get the status of product cover pages
 *
 * @returns Status of cover pages for each category
 */
export async function getCoverPageStatus(): Promise<CoverPageStatusResult | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/upload-cover-page`);
    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to get cover page status');
    }

    return result.coverPages as CoverPageStatusResult || null;
  } catch (error) {
    console.error('Get cover page status error:', error);
    return null;
  }
}

/**
 * Upload a product cover page
 *
 * @param file - The PDF file to upload
 * @param category - Product category ('structural-floor' or 'underlayment')
 * @returns Upload response
 */
export async function uploadCoverPage(
  file: File,
  category: ProductType
): Promise<{ success: boolean; error?: string }> {
  try {
    // Validate file type
    if (file.type !== 'application/pdf') {
      return { success: false, error: 'Only PDF files are allowed' };
    }

    // Validate file size (10MB max for cover pages)
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      return { success: false, error: 'Cover page must be under 10MB' };
    }

    // Convert to base64
    const base64 = await fileToBase64(file);

    const response = await fetch(`${API_BASE_URL}/upload-cover-page`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category,
        fileData: base64,
        fileName: file.name,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { success: false, error: result.error || 'Upload failed' };
    }

    return { success: true };
  } catch (error) {
    console.error('Upload cover page error:', error);
    return { success: false, error: 'Failed to upload cover page' };
  }
}

/**
 * Get the preview URL for a cover page
 *
 * @param category - Product category
 * @returns URL for previewing the cover page
 */
export function getCoverPagePreviewUrl(
  category: ProductType
): string {
  return `${API_BASE_URL}/get-document-url?key=${encodeURIComponent(`cover-pages/${category}.pdf`)}`;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Convert a File to base64 string
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data:application/pdf;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Format file size in human-readable format
 *
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}

/**
 * Validate a PDF file
 *
 * @param file - The file to validate
 * @returns Validation result
 */
export function validatePdfFile(file: File): { valid: boolean; error?: string } {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  if (file.type !== 'application/pdf') {
    return { valid: false, error: 'Only PDF files are allowed' };
  }

  const MAX_SIZE = 50 * 1024 * 1024; // 50MB
  if (file.size > MAX_SIZE) {
    return { valid: false, error: 'File size exceeds 50MB limit' };
  }

  return { valid: true };
}
